import { useContextMenuMESF } from "@dexteel/mesf-core";
import Grid2 from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { Clip } from "@/shared/models/Clip";
import { useTableData } from "../hooks/useTableData";
import { NewClipStatusBar } from "./NewClipStatusBar";

type Props = {
	isLoading: boolean;
	rows: Clip[];
	quickFilterText: string;
	selectedClipId: number | null;
	onSelectionChange: (clipId: number | null) => void;
	onOpenEdit: (clip: Clip) => void;
	onOpenDelete: (clip: Clip) => void;
	onOpenCreate: () => void;
};

export const ClipsTable = ({
	isLoading,
	rows,
	quickFilterText,
	selectedClipId,
	onSelectionChange,
	onOpenEdit,
	onOpenDelete,
	onOpenCreate,
}: Props) => {
	const gridApiRef = useRef<GridApi | null>(null);
	const didInitialScrollRef = useRef(false);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs, getMenuOptions } = useTableData({
		onOpenEdit,
		onOpenDelete,
		onOpenCreate,
		showContextMenu,
	});

	const statusBar = useMemo(
		() => ({
			statusPanels: [
				{
					statusPanel: NewClipStatusBar,
					statusPanelParams: {
						onNewClip: onOpenCreate,
					},
					align: "right" as const,
				},
			],
		}),
		[onOpenCreate],
	);

	const onRowDoubleClicked = (event: any) => {
		if (event.data) onOpenEdit(event.data);
	};

	const onRowSelected = useCallback(
		(event: RowSelectedEvent<Clip>) => {
			if (!event.node.isSelected()) return;
			const id = event.data?.ClipId ?? null;
			onSelectionChange(id);
		},
		[onSelectionChange],
	);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "clipsContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[showContextMenu],
	);

	useEffect(() => {
		registerConfig({
			id: "clipsContext",
			getOptions: getMenuOptions,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// One-shot: scroll to + select the URL clipId on initial mount once data
	// and the grid are ready. Subsequent URL changes don't re-trigger this.
	useEffect(() => {
		if (didInitialScrollRef.current) return;
		if (!gridApiRef.current) return;
		if (!rows || rows.length === 0) return;
		if (selectedClipId == null) {
			didInitialScrollRef.current = true;
			return;
		}
		const node = gridApiRef.current.getRowNode(String(selectedClipId));
		if (!node) {
			didInitialScrollRef.current = true;
			return;
		}
		gridApiRef.current.ensureNodeVisible(node, "middle");
		node.setSelected(true, true);
		didInitialScrollRef.current = true;
	}, [rows, selectedClipId]);

	return (
		<Grid2
			container
			size={{ md: 12, xs: 12 }}
			sx={{
				height: "68vh",
				minHeight: "68vh",
			}}
			spacing={1}
			component={Paper}
			elevation={2}
		>
			<Grid2 size={{ md: 12, xs: 12 }} height={"100%"}>
				<AgGridReact<Clip>
					loading={isLoading}
					rowData={rows}
					columnDefs={columnDefs}
					animateRows
					getContextMenuItems={getContextMenuItems}
					onRowDoubleClicked={onRowDoubleClicked}
					onRowSelected={onRowSelected}
					onGridReady={(params) => {
						gridApiRef.current = params.api;
					}}
					loadingOverlayComponent={CenteredLazyLoading}
					getRowId={(params) => `${params.data.ClipId}`}
					quickFilterText={quickFilterText}
					statusBar={statusBar}
					gridOptions={{
						rowSelection: {
							mode: "singleRow",
							checkboxes: false,
							enableClickSelection: true,
						},
					}}
				/>
			</Grid2>
		</Grid2>
	);
};
